import React, { useCallback, useEffect } from 'react';
import { Dialog, DialogButtons, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import { useTranslation } from 'react-i18next';
import {
    DobButton,
    DobCheckbox,
    DobTable,
    DobTableBody,
    DobTableDataCell,
    DobTableHeader,
    DobTableHeaderCell,
    DobTableRow,
} from '@dedalusdiit/dob-components';
import { ProcedureReportInformation } from '@/Models/PopupProcedureReportInformation';
import WorklistStore from '@/Pages/GenericWorklist/Stores/WorklistStore';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { useSystemSettingsContext } from '@/Contexts/App/SystemSettingsContext';
import UpgradeProcedureDialog from '@/Pages/GenericWorklist/Components/Dialogs/ReportDialog/UpgradeProcedureDialog';
import { navigateToReport } from '@/Pages/GenericWorklist/Logic/ReportNavigation';
import BulkType from '@/Models/Reports/BulkType';
import * as API from '@/API';

export interface ReportDialogProps {
    worklistStore: WorklistStore;
    onClose: () => void;
}

interface ProcedureSelectionCellProps {
    procedure: ProcedureReportInformation;
    worklistStore: WorklistStore;
}

const ProcedureSelectionCell = observer((props: Readonly<ProcedureSelectionCellProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { procedure, worklistStore } = props;
    const settingsContext = useSystemSettingsContext();
    const reportController = settingsContext.getValue('ReportController')!;

    if (procedure.Locked.Locked) {
        return <DobTableDataCell>{t('LockedBy')} {procedure.Locked.LockedBy}</DobTableDataCell>;
    }

    if (procedure.HasReport) {
        return <DobTableDataCell>{procedure.Report_UID}</DobTableDataCell>;
    }

    if (procedure.UpgradeAvailable) {
        return (
            <DobTableDataCell>
                <DobButton
                    color="silent"
                    size="small"
                    onClick={() => {
                        worklistStore.setReportProcedureUpgradeDialogOpen(true);
                        worklistStore.setReportProcedureToBeUpgraded(procedure);
                    }}
                >
                    {t('Upgrade')}
                </DobButton>
            </DobTableDataCell>
        );
    }

    if (!procedure.AvailableForReport) {
        return <DobTableDataCell />;
    }

    return (
        <DobTableDataCell>
            <DobCheckbox
                checked={procedure.Checked}
                onCheckedChange={(checked) => worklistStore.toggleReportProcedureChecked(procedure, checked as boolean)}
                disabled={reportController == 'Order'}
            />
        </DobTableDataCell>
    );
});

const ReportDialog = observer((props: Readonly<ReportDialogProps>): React.ReactElement => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const settingsContext = useSystemSettingsContext();
    const { worklistStore } = props;

    const onSkip = async (): Promise<void> => {
        const orderId = worklistStore.reportDialogInfo?.OrderId;
        if (!orderId) {
            return;
        }

        const response = await API.bulkSession.skipOrderInBulkSession(orderId);
        if (!response.AllOk) {
            console.warn('Skipping Order in bulk session was not successful. Check server logs');
            console.warn('Stopping bulk action to go to the next report');
            return;
        }
        if (response.Message) {
            console.warn('Message from Skip Order in bulk session:', response.Message);
            console.warn('Stopping bulk action to go to the next report');
            return;
        }

        worklistStore.setReportDialogOpen(false);

        // Find the next action to process: locate the current dictate action and take the one after it.
        // The next action may be a validate for the same order, or a dictate for the next order.
        const allActions = worklistStore.classifySelectedOrders();
        const currentDictateIndex = allActions.findIndex((a) => a.type === 'dictate' && a.orderId === orderId);
        const nextAction = currentDictateIndex >= 0 ? allActions[currentDictateIndex + 1] : undefined;
        if (!nextAction) {
            return;
        }

        const reportController = settingsContext.getValue('ReportController')!;
        if (nextAction.type === 'validate') {
            navigateToReport(navigate, nextAction.orderId, nextAction.reportId, reportController, undefined, true, BulkType.Mixed);
        }
        else {
            await worklistStore.openReportDialog(nextAction.orderId);
        }
    };

    const startReporting = useCallback((): void => {
        const reportController = settingsContext.getValue('ReportController')!;
        const reportDialogInfo = worklistStore.reportDialogInfo;
        if (!reportDialogInfo) {
            return;
        }

        const opIds = worklistStore.reportDialogProcedures?.filter((x) => x.Checked && x.AvailableForReport && !x.UpgradeAvailable && !x.Locked.Locked).map((x) => x.OrderHasProcedure_UID);
        const fallbackReportId = Number(reportDialogInfo.NewReportRecordID);
        const reportId = reportDialogInfo.ReportRecordID || (Number.isNaN(fallbackReportId) ? undefined : fallbackReportId);

        const bulk = worklistStore.selectedIds.length > 1;
        const bulkType = BulkType.Mixed;

        navigateToReport(
            navigate,
            reportDialogInfo.OrderId,
            reportId,
            reportController,
            opIds,
            bulk,
            bulkType,
        );
    }, [navigate, settingsContext, worklistStore]);

    const orderNumber = worklistStore.getOrderById(worklistStore.reportDialogInfo?.OrderId ?? -1)?.OrderInfo.OrderNumber;

    useEffect(() => {
        if (worklistStore.reportDialogProcedures?.length === 1 && worklistStore.reportDialogProcedures[0].HasReport) {
            startReporting(); // Which is technically an early-bail stating we're not interested in the information and just get on with it
        }
    }, [worklistStore.reportDialogProcedures]);

    return (
        <>
            <Dialog open={worklistStore.reportDialogOpen}>
                <DialogTitle>
                    <span>{t('Order')} #{orderNumber ?? ''} - {t('TitleProcedureCombinationReport')}</span>
                </DialogTitle>
                <DialogContent compact>
                    <DobTable clickableRows={false}>
                        <DobTableHeader>
                            <DobTableRow>
                                <DobTableHeaderCell align="center">{t('All')}</DobTableHeaderCell>
                                <DobTableHeaderCell align="center">{t('Procedure')}</DobTableHeaderCell>
                                <DobTableHeaderCell align="center">{t('Status')}</DobTableHeaderCell>
                                <DobTableHeaderCell align="center">{t('Assigned')}</DobTableHeaderCell>
                                <DobTableHeaderCell align="center">{t('Dictated')}</DobTableHeaderCell>
                                <DobTableHeaderCell align="center">{t('Validated')}</DobTableHeaderCell>
                            </DobTableRow>
                        </DobTableHeader>
                        <DobTableBody>
                            {worklistStore.reportDialogProcedures?.map((x) => (
                                <DobTableRow key={x.OrderHasProcedure_UID}>
                                    <ProcedureSelectionCell procedure={x} worklistStore={worklistStore} />
                                    <DobTableDataCell
                                        align="center"
                                        style={{ backgroundColor: x.Color, color: 'black' }}
                                    >
                                        <b>{x.Modality_Text}</b>
                                        {' '}
                                        {x.Code}
                                    </DobTableDataCell>
                                    <DobTableDataCell align="center">{x.Status_Text}</DobTableDataCell>
                                    <DobTableDataCell align="center">{x.Radiologist_Text}</DobTableDataCell>
                                    <DobTableDataCell align="center">{x.DictatedBy ?? '-'}</DobTableDataCell>
                                    <DobTableDataCell align="center">{x.ValidatedBy ?? '-'}</DobTableDataCell>
                                </DobTableRow>
                            ))}
                        </DobTableBody>
                    </DobTable>
                </DialogContent>
                <DialogButtons>
                    { worklistStore.selectedIds.length > 1
                        ? (
                                <DobButton
                                    color="danger"
                                    onClick={() => {
                                        void onSkip();
                                    }}
                                >
                                    {t('Skip')}
                                </DobButton>
                            )
                        : <></>}
                    <DobButton
                        color="secondary"
                        onClick={props.onClose}
                    >
                        {t('Close')}
                    </DobButton>
                    <DobButton
                        onClick={() => startReporting()}
                        disabled={worklistStore.reportDialogProcedures?.filter((x) => x.Checked && x.AvailableForReport && !x.UpgradeAvailable && !x.Locked.Locked).length === 0}
                    >
                        {t('Ok')}
                    </DobButton>
                </DialogButtons>
            </Dialog>

            <UpgradeProcedureDialog
                open={worklistStore.reportUpgradeProcedureDialogOpen}
                worklistStore={worklistStore}
            />
        </>
    );
});

export default ReportDialog;
